import React from 'react'
import { motion, useScroll, useSpring } from 'framer-motion'

const ACCENTS = [
  'from-indigo-500 to-sky-400',
  'from-violet-500 to-fuchsia-400',
  'from-sky-500 to-cyan-400',
  'from-emerald-500 to-teal-400',
  'from-amber-500 to-orange-400',
  'from-rose-500 to-pink-400',
]

// Heroicons-style outline paths, one per section
const ICONS = {
  user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  chart: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  cookie: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  spark: 'M13 10V3L4 14h7v7l9-11h-7z',
  lock: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  share: 'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z',
  link: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  check: 'M5 13l4 4L19 7',
  mail: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
}

const SECTIONS = [
  {
    icon: 'user',
    title: 'Personal identification information',
    p: ['We may collect personal identification information from Users in a variety of ways, including, but not limited to, when Users visit our site, subscribe to the newsletter, fill out a form, and in connection with other activities, services, features or resources we make available on our Site. Users may be asked for, as appropriate, name, email address, mailing address, phone number. Users may, however, visit our Site anonymously. We will collect personal identification information from Users only if they voluntarily submit such information to us. Users can always refuse to supply personally identification information, except that it may prevent them from engaging in certain Site related activities.'],
  },
  {
    icon: 'chart',
    title: 'Non-personal identification information',
    p: ['We may collect non-personal identification information about Users whenever they interact with our Site. Non-personal identification information may include the browser name, the type of computer and technical information about Users means of connection to our Site, such as the operating system and the Internet service providers utilized and other similar information.'],
  },
  {
    icon: 'cookie',
    title: 'Web browser cookies',
    p: ['Our Site may use "cookies" to enhance User experience. User\'s web browser places cookies on their hard drive for record-keeping purposes and sometimes to track information about them. User may choose to set their web browser to refuse cookies, or to alert you when cookies are being sent. If they do so, note that some parts of the Site may not function properly.'],
  },
  {
    icon: 'spark',
    title: 'How we use collected information',
    p: ['Vector Visionary may collect and use Users personal information for the following purposes:'],
    list: [
      ['To improve customer service:', 'Information you provide helps us respond to your customer service requests and support needs more efficiently.'],
      ['To personalize user experience:', 'We may use information in the aggregate to understand how our Users as a group use the services and resources provided on our Site.'],
      ['To improve our Site:', 'We may use feedback you provide to improve our products and services.'],
      ['To send periodic emails:', 'We may use the email address to send User information and updates pertaining to their order. It may also be used to respond to their inquiries, questions, and/or other requests.'],
    ],
  },
  {
    icon: 'lock',
    title: 'How we protect your information',
    p: ['We adopt appropriate data collection, storage and processing practices and security measures to protect against unauthorized access, alteration, disclosure or destruction of your personal information, username, password, transaction information and data stored on our Site.'],
  },
  {
    icon: 'share',
    title: 'Sharing your personal information',
    p: ['We do not sell, trade, or rent Users personal identification information to others. We may share generic aggregated demographic information not linked to any personal identification information regarding visitors and users with our business partners, trusted affiliates and advertisers for the purposes outlined above.'],
  },
  {
    icon: 'link',
    title: 'Third party websites',
    p: ['Users may find advertising or other content on our Site that link to the sites and services of our partners, suppliers, advertisers, sponsors, licensors and other third parties. We do not control the content or links that appear on these sites and are not responsible for the practices employed by websites linked to or from our Site.'],
  },
  {
    icon: 'refresh',
    title: 'Changes to this privacy policy',
    p: ['Vector Visionary has the discretion to update this privacy policy at any time. When we do, we will revise the updated date at the bottom of this page. We encourage Users to frequently check this page for any changes to stay informed about how we are helping to protect the personal information we collect.'],
  },
  {
    icon: 'check',
    title: 'Your acceptance of these terms',
    p: ['By using this Site, you signify your acceptance of this policy. If you do not agree to this policy, please do not use our Site. Your continued use of the Site following the posting of changes to this policy will be deemed your acceptance of those changes.'],
  },
  {
    icon: 'mail',
    title: 'Contacting us',
    p: ['If you have any questions about this Privacy Policy, the practices of this site, or your dealings with this site, please contact us at:'],
    contact: true,
  },
]

const Icon = ({ d, className = 'h-6 w-6' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
)

const PrivacyPage = () => {
  const { scrollYProgress } = useScroll()
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30 })

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Scroll progress */}
      <motion.div style={{ scaleX: progress }} className="fixed left-0 top-0 z-50 h-1 w-full origin-left bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-sky-400" />

      {/* Hero */}
      <header className="relative overflow-hidden bg-slate-950 px-4 pb-32 pt-24 text-center">
        <div className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full bg-indigo-600/40 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-sky-500/30 blur-3xl" />
        <div className="pointer-events-none absolute left-1/2 top-10 h-72 w-72 -translate-x-1/2 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="relative mx-auto max-w-3xl">
          <div className="mx-auto mb-8 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-indigo-500 to-sky-400 shadow-2xl shadow-indigo-500/40 ring-4 ring-white/10">
            <Icon className="h-10 w-10 text-white" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </div>
          <h1 className="bg-gradient-to-r from-white via-indigo-200 to-sky-300 bg-clip-text text-5xl font-black tracking-tight text-transparent md:text-7xl">
            Privacy Policy
          </h1>
          <p className="mt-6 inline-block rounded-full bg-white/10 px-6 py-2 text-lg font-medium text-slate-200 ring-1 ring-white/20 backdrop-blur">
            Your security is our priority
          </p>
        </motion.div>
      </header>

      <div className="relative mx-auto -mt-20 max-w-4xl px-4 pb-24">
        {/* Intro card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="rounded-3xl bg-white p-8 shadow-2xl shadow-indigo-900/10 ring-1 ring-slate-900/5 md:p-12"
        >
          <p className="text-xl font-medium leading-relaxed text-slate-800">
            This Privacy Policy governs the manner in which Vector Visionary collects, uses, maintains and discloses information collected from users (each, a "User") of our website. This privacy policy applies to our website and all products and services offered by Vector Vision.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="mt-10 space-y-8">
          {SECTIONS.map((s, i) => {
            const accent = ACCENTS[i % ACCENTS.length]
            return (
              <section key={s.title} className="rounded-3xl bg-white p-8 shadow-lg ring-1 ring-slate-900/5 md:p-10">
                <div className="mb-6 flex items-center gap-4">
                  <span className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${accent} text-white shadow-lg`}>
                    <Icon d={ICONS[s.icon]} />
                  </span>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">{s.title}</h2>
                </div>
                <div className={`mb-6 h-1 w-full rounded-full bg-gradient-to-r ${accent} opacity-20`} />
                <div className="space-y-5">
                  {s.p.map((t) => <p key={t} className="text-[1.05rem] leading-relaxed text-slate-600">{t}</p>)}

                  {s.list && (
                    <ul className="grid list-none grid-cols-1 gap-3 pl-0 sm:grid-cols-2">
                      {s.list.map(([lead, body]) => (
                        <li key={lead} className="flex items-start gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200 transition duration-200 hover:-translate-y-0.5 hover:shadow-md hover:ring-indigo-400">
                          <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500 text-white">
                            <Icon className="h-3.5 w-3.5" d={ICONS.check} />
                          </span>
                          <span className="leading-relaxed text-slate-600"><strong className="text-slate-900">{lead}</strong> {body}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {s.contact && (
                    <div className="relative overflow-hidden rounded-3xl bg-slate-900 p-8 text-white shadow-2xl">
                      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-indigo-500/40 blur-3xl" />
                      <div className="pointer-events-none absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-sky-400/30 blur-3xl" />
                      <div className="relative space-y-1">
                        <p className="mb-3 bg-gradient-to-r from-indigo-300 to-sky-300 bg-clip-text text-2xl font-extrabold text-transparent">Vector Visionary</p>
                        <p className="text-slate-300">Contact us through Email.</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )
          })}
        </div>

        <div className="mt-12 border-t border-slate-200 pt-8 text-center text-sm text-slate-400">
          <p>This document was last updated on {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPage